import useLocalStorage from '@rehooks/local-storage';
import { useEffect, useMemo } from 'react';
import {
  EnvironmentTier,
  getEnvironmentTier,
} from './utils/getEnvironmentTier';
import { useLighthousePreference } from './hooks/useLighthousePreference';
import { useNavigationVariant } from './hooks/useNavigationVariant';
import { NavigationOverrides } from './NavigationContext';
import { isSalesforceHost } from './utils/isSalesforceHost';

declare global {
  interface Window {
    __STORYBOOK_GLOBAL_NAV_OVERRIDE?: boolean;
  }
}

export const isEnvironmentFlagTruthy = (
  FLAGS: {
    DEVELOPMENT: boolean;
    STAGING: boolean;
    PREPROD: boolean;
    SANDBOX: boolean;
    PRODUCTION: boolean;
  },
  hostname = window.location.hostname,
): boolean => {
  switch (getEnvironmentTier(hostname)) {
    case EnvironmentTier.DEV:
      return FLAGS.DEVELOPMENT;
    case EnvironmentTier.STG:
      return FLAGS.STAGING;
    case EnvironmentTier.PREPROD:
      return FLAGS.PREPROD;
    case EnvironmentTier.SBX:
      return FLAGS.SANDBOX;
    default:
      return FLAGS.PRODUCTION;
  }
};

/* Truthy provides Legacy Nav */
export const getEngineeringLegacyNavFlag = (): boolean => {
  const FLAGS = {
    DEVELOPMENT: false,
    STAGING: false,
    PREPROD: false,
    SANDBOX: false,
    PRODUCTION: false,
  };

  return isEnvironmentFlagTruthy(FLAGS);
};

/* Truthy provides Icon Button Link to Replatformed Experience */
export const getReplatformedLinkFlag = (): boolean => {
  const FLAGS = {
    DEVELOPMENT: true,
    STAGING: true,
    PREPROD: true,
    SANDBOX: false,
    PRODUCTION: false,
  };

  return isEnvironmentFlagTruthy(FLAGS);
};

export enum LocalStorageFeatureFlags {
  UILabs = 'ZNav.UILabsMenu',
  AppSwitcher = 'ZNav.AppSwitcher',
  GlobalNav = 'ZNav.GlobalNavEnabled',
  ForceDisableGlobalNav = 'zNav.forceDisableGlobalNav',
  ForceEnableGlobalNav = 'zNav.forceEnableGlobalNav',
}

/* Truthy provides bell icon in AppBar for "Notifications" */
export const useNotificationsFlag = () =>
  useLocalStorage<boolean>(
    'ZNav.Notifications',
    isEnvironmentFlagTruthy({
      DEVELOPMENT: true,
      STAGING: true,
      PREPROD: true,
      SANDBOX: true,
      PRODUCTION: true,
    }),
  );

/* Truthy provides new nav App Switcher */
export const useAppSwitcherFlag = () => {
  const featureFlags = {
    DEVELOPMENT: true,
    STAGING: true,
    PREPROD: true,
    SANDBOX: true,
    PRODUCTION: true,
  };
  const [isAppSwitcherEnabled, writeStorage] = useLocalStorage<boolean>(
    LocalStorageFeatureFlags.AppSwitcher,
    isEnvironmentFlagTruthy(featureFlags),
  );
  // update stale local storage flag on mount
  useEffect(() => {
    writeStorage(isEnvironmentFlagTruthy(featureFlags));
  }, []);
  return [isAppSwitcherEnabled];
};

/* Truthy provides question mark icon in AppBar for "Help" */
export const useSupportDeflectionFlag = () =>
  useLocalStorage<boolean>(
    'ZNav.Deflection',
    isEnvironmentFlagTruthy({
      DEVELOPMENT: true,
      STAGING: true,
      PREPROD: true,
      SANDBOX: true,
      PRODUCTION: true,
    }),
  );

/* Truthy provides top banner for opt-out of new UI (by session) */
export const useOptOutBannerFlag = () =>
  useLocalStorage<boolean>(
    'ZNav.OptOut',
    isEnvironmentFlagTruthy({
      DEVELOPMENT: true,
      STAGING: true,
      PREPROD: true,
      SANDBOX: false,
      PRODUCTION: true,
    }),
  );

export const useSupportDeflectionRevenueFlag = () =>
  useLocalStorage<boolean>(
    'ZNav.RevenueDeflection',
    isEnvironmentFlagTruthy({
      DEVELOPMENT: true,
      STAGING: true,
      PREPROD: true,
      SANDBOX: true,
      PRODUCTION: true,
    }),
  );

export const useAppsmithSolutionsFlag = () =>
  useLocalStorage<boolean>(
    'ZNav.AppsmithSolutions',
    isEnvironmentFlagTruthy({
      DEVELOPMENT: true,
      STAGING: true,
      PREPROD: true,
      SANDBOX: true,
      PRODUCTION: true,
    }),
  );

/* Truthy provides new global nav */
export const useGlobalNavFlag = ({
  overrides,
}: {
  overrides?: NavigationOverrides;
}) => {
  const [isForceDisableGlobalNav] = useLocalStorage<boolean>(
    LocalStorageFeatureFlags.ForceDisableGlobalNav,
    false,
  );

  const [isForceEnableGlobalNav] = useLocalStorage<boolean>(
    LocalStorageFeatureFlags.ForceEnableGlobalNav,
    false,
  );

  const { isLighthouseConfigEnabled, isLoading, isError, tenantId, userId } =
    useLighthousePreference(overrides, 'ZNavIdentityLighthousePreference');

  const shouldEnableFlag = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    const conditions = {
      tenantConfig: isLighthouseConfigEnabled,
      notForceDisabled: !isForceDisableGlobalNav,
    };
    const allConditionsMet = Object.values(conditions).every(Boolean);
    return allConditionsMet || isForceEnableGlobalNav;
  }, [
    isLighthouseConfigEnabled,
    isForceDisableGlobalNav,
    isForceEnableGlobalNav,
    isLoading,
  ]);

  const variant = useNavigationVariant(overrides);

  /* Tenant-scoped, so a load that has not resolved identity yet reads a key
   * ending in `_undefined_undefined` and can never hit. */
  const storageKey = `${LocalStorageFeatureFlags.GlobalNav}_${tenantId}_${userId}`;
  const [cachedEnabled, writeCached] = useLocalStorage<boolean>(storageKey);

  /* Not tenant-scoped, so it is readable on the loads where the key above is
   * not: the last answer this browser resolved, for this variant. Billing and
   * Revenue resolve the flag from different sources, so they must not share it. */
  const lastResolvedKey = `${LocalStorageFeatureFlags.GlobalNav}.last.${variant}`;
  const [lastResolvedEnabled, writeLastResolved] =
    useLocalStorage<boolean>(lastResolvedKey);

  /* A branch has to render before the config resolves. Prefer this tenant's own
   * cached answer, fall back to whatever this browser last resolved, and only
   * then guess.
   *
   * The guess is Global Nav — but only for a real Billing/Revenue load whose
   * identity is still in flight. Nothing about a first-ever load says which
   * branch is right, so this bets on where the rollout is heading, and the two
   * outcomes are not symmetric: guessing right costs nothing and starts every
   * request in useNavigationData in parallel with the config, while guessing
   * legacy refetches /v1/navigation under `ZNavMenu`, a key Global Nav does
   * not share.
   *
   * Two mounts must not get that guess. A failed config will never produce a
   * better answer, and writing the failure is already skipped, so keeping
   * `?? true` would leave Global Nav up forever. A Salesforce LWC host is
   * not a Billing page — it only wants the agent — and `/v1/navigation`
   * never arrives there, so the guess would pin NavigationLoadingSkeleton
   * next to the chat. */
  const canGuessGlobalNav = !isError && !isSalesforceHost();
  const bet = cachedEnabled ?? lastResolvedEnabled ?? canGuessGlobalNav;

  useEffect(() => {
    /* An unresolved or failed config leaves shouldEnableFlag false, so writing
     * it would cache a wrong "off" — and for the shared hint below that would
     * follow the user onto every later load. */
    if (isLoading || isError || cachedEnabled === shouldEnableFlag) {
      return;
    }
    writeCached(!!shouldEnableFlag);
  }, [shouldEnableFlag, isLoading, isError, cachedEnabled, writeCached]);

  useEffect(() => {
    if (isLoading || isError || lastResolvedEnabled === shouldEnableFlag) {
      return;
    }
    writeLastResolved(!!shouldEnableFlag);
  }, [
    shouldEnableFlag,
    isLoading,
    isError,
    lastResolvedEnabled,
    writeLastResolved,
  ]);

  if (
    typeof window !== 'undefined' &&
    window.__STORYBOOK_GLOBAL_NAV_OVERRIDE !== undefined
  ) {
    return {
      isGlobalNavEnabled: window.__STORYBOOK_GLOBAL_NAV_OVERRIDE,
      isGlobalNavConfirmed: !!window.__STORYBOOK_GLOBAL_NAV_OVERRIDE,
      isLoading: false,
      isError: false,
    };
  }

  return {
    isGlobalNavEnabled: bet || shouldEnableFlag,
    /* `isGlobalNavEnabled` can be a guess: while the config is in flight it is
     * whatever `bet` resolved to, and that can be stale for a tenant since
     * switched off. This is the answer the server actually gave, for the few
     * things that must not act on a guess. */
    isGlobalNavConfirmed: !isLoading && !isError && !!shouldEnableFlag,
    isLoading,
    isError,
  };
};
